import { useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { journeyActions } from '../../store/journeySlice';
import {
  calculateBulkPremium,
  getPremiumResultForDimensions,
  buildRiderOverridesFromSelection,
  buildRopEcboOverrides,
  calculateNishchitBulkPremiumFromLead,
  resolveApplicationNumberForPremium,
} from '../../api/premiumService';
import { initQuote } from '../../api/quoteService';
import { PLAN_NAMES_NISCHIT_PENSION, PLAN_PRODUCT_CODES_NISCHIT_PENSION, PAYMENT_TERM_TYPE, getDefaultCoverAmount, getLimitedPayOptionsForPlan, MIN_COVER_ELITE, MIN_COVER_PLUS, COVER_AMOUNT_RUPEE_STEP } from '../../constants';
import { calculateAge } from '../utils/formatters';
import { effectiveMaxPolicyTermForPlan } from '../utils/planTermCaps';
import { initiatePayment, buildPaymentRequest } from '../../api/paymentService';
import { PAYMENT_FLOW, getJourneyData } from '../../api/journeyService';
import {
  extractApplicationNumberFromResponse,
  extractCaseIdFromResponse,
  getStoredApplicationNumber,
  setStoredApplicationNumber,
  setStoredCaseId,
  saveJourneySnapshot,
} from '../utils';
import { isSupersededPremiumRequestError } from '../utils/premiumRequestErrors';
import { hydrateStoreFromJourney } from '../utils/journeyResume';
import { logError } from '../utils/logger';
import {
  resolveNischitProductCode,
  resolveNischitPptAndDp,
  getNischitVisiblePlanOptionIds,
  isNischitSecondaryAnnuitantComplete,
} from '../../insurance-journey/quote/productConfig';
import { getCurrentJourneyContext } from '../utils/journeyContextRuntime';

const MIN_PREMIUM_LOADING_MS = 250;

/**
 * Strip Nishchit row-selection fields before bulk calculate.
 * `planOptions` in the bulk payload already lists every option; keeping `selectedNishchitPlanId` /
 * `planOption` in `additionalInputFields` changes the serialized request when the user only clicks
 * another row in `selectedNishchitPlans`, which must not trigger a new bulk API call.
 */
const stripNischitPlanOptionFromOverrides = (overrides = {}) => {
  const add = { ...(overrides.additionalInputFields || {}) };
  delete add.planOption;
  delete add.planOptionCode;
  delete add.selectedNishchitPlanId;
  return { ...overrides, additionalInputFields: add };
};

/** Merge quote-init over lead so bulk premium matches quote page `effectiveLeadResult` + keeps `leadInput`. */
const mergeLeadAndQuoteInitForPremium = (leadRes, quoteInit) => {
  if (leadRes && quoteInit) return { ...leadRes, ...quoteInit };
  return quoteInit || leadRes;
};

/**
 * Resolve selected Nishchit planOption id.
 * Call sites pass (customizePlanData, plan [, productCode]) — keep that order.
 * Joint defaults: 22070 → option_4, 22074 → option_5.
 */
const resolveNischitPlanOptionId = (customize = {}, plan, productCode) => {
  const isJointPlan = plan === PLAN_NAMES_NISCHIT_PENSION.PLAN_B;
  const defaultJoint = String(productCode) === '22074' ? 'option_5' : 'option_4';
  return String(
    customize?.planOption ||
    customize?.planOptionCode ||
    customize?.selectedNishchitPlanId ||
    (isJointPlan ? defaultJoint : 'option_1'),
  );
};

/** Normalized pincode flags; null snapshot → both allowed (legacy / not yet evaluated). */
const normalizePincodeTermPlanAvailability = (pin) => {
  if (pin == null || typeof pin !== 'object') {
    return { plusAllowed: true, eliteAllowed: true };
  }
  return {
    plusAllowed: pin.plusAllowed !== false,
    eliteAllowed: pin.eliteAllowed !== false,
  };
};

const getRulesEliteFromAssignedName = (assignedProductName) =>
  String(assignedProductName ?? '').toLowerCase().includes('elite');

/**
 * ECBO / workbook rules: treat as Elite-capable only if API assigns Elite and pincode master allows Elite.
 */
const getIsEliteEligibleForPremium = (pincodeAvailability, assignedProductName) => {
  const pin = normalizePincodeTermPlanAvailability(pincodeAvailability);
  return getRulesEliteFromAssignedName(assignedProductName) && pin.eliteAllowed;
};

/** First bulk plan on quote entry from pincode row + assigned product name. */
const resolveBulkPlanOnQuoteEntry = (pincodeAvailability, assignedProductName) => {
  const pin = normalizePincodeTermPlanAvailability(pincodeAvailability);
  const rulesElite = getRulesEliteFromAssignedName(assignedProductName);
  if (pin.eliteAllowed && pin.plusAllowed) {
    return rulesElite ? PLAN_NAMES_NISCHIT_PENSION.PLAN_B : PLAN_NAMES_NISCHIT_PENSION.PLAN_A;
  }
  if (pin.eliteAllowed && !pin.plusAllowed) {
    return PLAN_NAMES_NISCHIT_PENSION.PLAN_B;
  }
  return PLAN_NAMES_NISCHIT_PENSION.PLAN_A;
};

/** Generate the distinct cover amount values that Quote UI shows (mirrors generateCoverAmountOptions in Quote.jsx). */
const generateCoverAmountValues = (maxCoverAmount, plan) => {
  if (!maxCoverAmount) return [];
  const max = Number(maxCoverAmount);
  if (Number.isNaN(max)) return [];
  const isElite = plan === PLAN_NAMES_NISCHIT_PENSION.PLAN_B;
  const minCover = isElite ? MIN_COVER_ELITE : MIN_COVER_PLUS;
  const set = new Set();
  for (let i = 0; i < 4; i++) {
    const pct = 1 - i * 0.1;
    const value = Math.round((max * pct) / COVER_AMOUNT_RUPEE_STEP) * COVER_AMOUNT_RUPEE_STEP;
    const clamped = Math.min(max, Math.max(value, minCover));
    if (clamped > 0) set.add(clamped);
  }
  return [...set];
};

/** Attained age from lead snapshot or lead form (for Plus policy-term cap). */
const getAttainedAgeFromLead = (leadResult, formData) => {
  const dob =
    leadResult?.leadInput?.lifeAssuredDob ??
    formData?.dateOfBirth ??
    null;
  if (dob == null || String(dob).trim() === '') return null;
  const a = calculateAge(dob);
  if (a == null || Number.isNaN(Number(a))) return null;
  return Number(a);
};

/** Generate policy term values that Quote UI shows (mirrors generateSteppedOptions in Quote.jsx). */
const generatePolicyTermValues = (maxPolicyTerm) => {
  if (!maxPolicyTerm) return [];
  const max = Number(maxPolicyTerm);
  if (Number.isNaN(max)) return [];
  const values = [];
  for (let i = 0; i < 4; i++) {
    const value = max - i * 5;
    if (value > 0) values.push(value);
  }
  return values;
};

/** Build the cross-product of all (baseSumAssured, policyTerm) pairs for the bulk request. */
const buildAllVariations = (coverAmounts, policyTerms) => {
  const variations = [];
  for (const sa of coverAmounts) {
    for (const pt of policyTerms) {
      variations.push({ baseSumAssured: sa, policyTerm: pt });
    }
  }
  return variations;
};

const variationKey = (v) => `${Number(v.baseSumAssured)}_${Number(v.policyTerm)}`;

/** Union of variation lists by (SA, PT) so bulk includes grid + desired custom rows without duplicates. */
const mergeVariationSets = (lists) => {
  const map = new Map();
  lists.flat().forEach((v) => {
    if (v == null || v.baseSumAssured == null || v.policyTerm == null) return;
    map.set(variationKey(v), { baseSumAssured: Number(v.baseSumAssured), policyTerm: Number(v.policyTerm) });
  });
  return [...map.values()];
};

/** Policy terms shown on quote (stepped options) plus current PT if it is a desired/custom value. */
const resolvePolicyTermsForBulk = (leadPolicyTermOptions, activePt, plan, attainedAge) => {
  const maxForGrid = effectiveMaxPolicyTermForPlan(
    leadPolicyTermOptions,
    plan,
    attainedAge,
  );
  const terms = generatePolicyTermValues(
    maxForGrid != null ? maxForGrid : leadPolicyTermOptions,
  );
  const ptNum = Number(activePt);
  if (!terms.length) {
    return Number.isFinite(ptNum) ? [ptNum] : [];
  }
  if (Number.isFinite(ptNum) && !terms.some((t) => Number(t) === ptNum)) {
    return [...terms, ptNum];
  }
  return terms;
};

/** Active PT for cache/API: caps Plus at max term implied by cover-till-age 75 rule. */
const resolveActivePolicyTermForBulk = (leadResult, plan, policyTerm) => {
  const attainedAge = getAttainedAgeFromLead(leadResult, null);
  const rawPT = leadResult?.policyTermOptions;
  const ptFallback = Number((Array.isArray(rawPT) ? rawPT[0] : rawPT) ?? 25);
  const maxPtForPlan = effectiveMaxPolicyTermForPlan(rawPT, plan, attainedAge);
  const uncapped = Number(policyTerm ?? ptFallback);
  const pt =
    maxPtForPlan != null ? Math.min(uncapped, maxPtForPlan) : uncapped;
  return { pt, attainedAge };
};

/** Resolve a multi-dimension result from the 3-level cache: byPlan[plan][cover][policyTerm]. */
const getCachedMulti = (byPlan, plan, coverAmount, policyTerm) => {
  const coverNode = byPlan?.[plan]?.[String(coverAmount)];
  if (!coverNode) return null;
  if (policyTerm != null && coverNode[String(policyTerm)]) return coverNode[String(policyTerm)];
  return coverNode._default || null;
};

const toTitleFrequency = (value, fallback = 'Yearly') => {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) return fallback;
  if (raw.includes('month')) return 'Monthly';
  if (raw.includes('quarter')) return 'Quarterly';
  if (raw.includes('half')) return 'Half-yearly';
  if (raw.includes('year')) return 'Yearly';
  return fallback;
};

const firstNonBlank = (...values) =>
  values.find(
    (v) =>
      v !== undefined &&
      v !== null &&
      !(typeof v === 'string' && v.trim() === ''),
  );

const resolveUpdatedLeadPersonFields = (leadFormData = {}, leadInput = {}) => {
  const lifeAssuredDob =
    firstNonBlank(
      leadFormData?.lifeAssuredDob,
      leadFormData?.dateOfBirth,
      leadFormData?.dob,
      leadFormData?.proposerDob,
      leadInput?.lifeAssuredDob,
      leadInput?.dateOfBirth,
      leadInput?.dob,
      leadInput?.proposerDob,
      '',
    ) ?? '';
  const lifeAssuredGender =
    firstNonBlank(
      leadFormData?.lifeAssuredGender,
      leadFormData?.gender,
      leadFormData?.proposerGender,
      leadInput?.lifeAssuredGender,
      leadInput?.gender,
      leadInput?.proposerGender,
      '',
    ) ?? '';
  const primaryAnnuitantDob =
    firstNonBlank(
      leadFormData?.annuitantDateOfBirth,
      leadFormData?.primaryAnnuitantDob,
      leadInput?.annuitantDateOfBirth,
      leadInput?.primaryAnnuitantDob,
      lifeAssuredDob,
      '',
    ) ?? '';
  const primaryAnnuitantGender =
    firstNonBlank(
      leadFormData?.annuitantGender,
      leadFormData?.primaryAnnuitantGender,
      leadInput?.annuitantGender,
      leadInput?.primaryAnnuitantGender,
      lifeAssuredGender,
      '',
    ) ?? '';
  return {
    lifeAssuredDob,
    lifeAssuredGender,
    primaryAnnuitantDob,
    primaryAnnuitantGender,
    proposerDob:
      firstNonBlank(
        leadFormData?.proposerDob,
        lifeAssuredDob,
        leadInput?.proposerDob,
        '',
      ) ?? '',
    proposerGender:
      firstNonBlank(
        leadFormData?.proposerGender,
        lifeAssuredGender,
        leadInput?.proposerGender,
        '',
      ) ?? '',
  };
};

export const useJourney = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const state = useSelector((s) => s.journey);
  const wait = useCallback((ms) => new Promise((resolve) => setTimeout(resolve, ms)), []);
  const bulkAbortRef = useRef(null);

  const runWithPremiumLoading = useCallback(async (operation) => {
    const startedAt = Date.now();
    dispatch(journeyActions.beginPremiumLoading());
    try {
      return await operation();
    } finally {
      const remainingMs = MIN_PREMIUM_LOADING_MS - (Date.now() - startedAt);
      if (remainingMs > 0) {
        await wait(remainingMs);
      }
      dispatch(journeyActions.endPremiumLoading());
    }
  }, [dispatch, wait]);

  const buildJourneySpecificPremiumOverrides = useCallback((
    baseOverrides = {},
    {
      plan,
      premiumFrequency,
      policyTerm: _policyTerm,
      selectedLimitedPayTerm,
      leadFormDataOverride,
    } = {},
  ) => {
    const customize = state?.customizePlanData || {};
    const leadInputSnapshot =
      state?.leadResult?.leadInput || state?.quoteInitResult?.leadInput || {};
    const resolvedLeadPeople = resolveUpdatedLeadPersonFields(
      leadFormDataOverride || state?.leadFormData || {},
      leadInputSnapshot,
    );
    const isJointPlan = plan === PLAN_NAMES_NISCHIT_PENSION.PLAN_B;
    const payoutFrequency = toTitleFrequency(
      customize.annuityFrequency ?? premiumFrequency ?? 'Yearly',
      'Yearly',
    );
    const payFrequency = toTitleFrequency(
      customize.premiumFrequency ?? premiumFrequency ?? 'Yearly',
      'Yearly',
    );
    const planOption = String(
      customize.planOption ||
      customize.planOptionCode ||
      customize.selectedNishchitPlanId ||
      (isJointPlan ? 'option_4' : 'option_1'),
    );
    const resolvedProductCode = resolveNischitProductCode(
      leadFormDataOverride || state?.leadFormData || {},
    );
    const { ppt, dp } = resolveNischitPptAndDp(resolvedProductCode, customize, {
      premiumPayingTerm: selectedLimitedPayTerm,
    });
    const additionalInputFields = {
      ...customize,
      planOption,
      primaryAnnuitantDob: firstNonBlank(
        customize.primaryAnnuitantDob,
        resolvedLeadPeople.primaryAnnuitantDob,
      ),
      primaryAnnuitantGender: firstNonBlank(
        customize.primaryAnnuitantGender,
        resolvedLeadPeople.primaryAnnuitantGender,
      ),
      proposerDob: firstNonBlank(
        customize.proposerDob,
        resolvedLeadPeople.proposerDob,
      ),
      proposerGender: firstNonBlank(
        customize.proposerGender,
        resolvedLeadPeople.proposerGender,
      ),
      premium: Number(customize.premiumAmount ?? customize.premium ?? 220000),
      premiumAmount: Number(customize.premiumAmount ?? customize.premium ?? 220000),
      ppt,
      dp,
      annuityFrequency: payoutFrequency,
      premiumFrequency: payFrequency,
    };
    return {
      ...baseOverrides,
      productCode: resolvedProductCode,
      premiumFrequency: payFrequency,
      additionalInputFields,
    };
  }, [
    state?.customizePlanData,
    state?.leadFormData,
    state?.leadResult,
    state?.quoteInitResult,
  ]);

  /**
   * Fetch bulk premium for a plan: all cover x policyTerm variations in one API call.
   * Aborts any previous in-flight bulk request to free network resources.
   * Stores results in Redux 3-level cache.
   */
  const fetchBulkForPlan = useCallback(async (
    plan, leadResult, overrides, variations, premiumFrequency, paymentTermType, selectedLimitedPayTerm, activePT,
  ) => {
    if (bulkAbortRef.current) {
      bulkAbortRef.current.abort();
    }
    const controller = new AbortController();
    bulkAbortRef.current = controller;

    const isLP = paymentTermType === 'Limited Pay';
    const ppt = isLP ? (selectedLimitedPayTerm ?? 5) : (activePT ?? overrides.policyTerm);
    const applicationNumber = resolveApplicationNumberForPremium(leadResult, overrides, {
      applicationNumber: state.applicationNumber,
      storedApplicationNumber: getStoredApplicationNumber(),
    });
    const bulkResult = await calculateBulkPremium(
      leadResult,
      {
        ...overrides,
        ...(applicationNumber ? { applicationNumber } : {}),
        productCode:
          resolveNischitProductCode(state?.leadFormData || {}) ||
          PLAN_PRODUCT_CODES_NISCHIT_PENSION[plan] ||
          leadResult?.assignedProductCode,
        premiumFrequency: premiumFrequency || 'Yearly',
        premiumPayingOption: isLP ? 'Limited' : 'Regular',
        premiumPayingTerm: ppt,
      },
      variations,
      {
        primaryMode: premiumFrequency || 'Yearly',
        primaryTermType: isLP ? PAYMENT_TERM_TYPE.LIMITED_PAY : PAYMENT_TERM_TYPE.REGULAR_PAY,
        primaryPremiumPayingTerm: ppt,
        limitedPayOptions: getLimitedPayOptionsForPlan(plan, activePT ?? overrides.policyTerm),
        signal: controller.signal,
      },
    );
    const productCode = resolveNischitProductCode(state?.leadFormData || {});
    const isJointMain = plan === PLAN_NAMES_NISCHIT_PENSION.PLAN_B;
    const jointBucketReady =
      isJointMain && isNischitSecondaryAnnuitantComplete(state?.customizePlanData || {});
    const planIds = getNischitVisiblePlanOptionIds(productCode, jointBucketReady);
    const primaryPlanKey = jointBucketReady ? plan : PLAN_NAMES_NISCHIT_PENSION.PLAN_A;
    const planOptionId = resolveNischitPlanOptionId(
      state?.customizePlanData || {},
      primaryPlanKey,
      productCode,
    );
    const mergedLead = mergeLeadAndQuoteInitForPremium(state.leadResult, state.quoteInitResult) || leadResult;
    const map = await calculateNishchitBulkPremiumFromLead(
      mergedLead,
      stripNischitPlanOptionFromOverrides(overrides),
      planIds,
    );
    const premiumResult = map[planOptionId] || null;
    dispatch(journeyActions.setBulkPremiumResults({ plan, results: bulkResult.results || {} }));
    return { bulkResult, premiumResult };
  }, [
    dispatch,
    state.applicationNumber,
    state?.customizePlanData,
    state?.leadFormData,
    state?.leadResult,
    state?.quoteInitResult,
  ]);

  const handleLeadComplete = useCallback(async ({
    result,
    formData,
    applicationNumber: appNum,
    pincodeTermPlanAvailability: pincodeAvailArg,
  }) => {
    dispatch(journeyActions.leadComplete({ leadResult: result, formData }));
    navigate('/quote');

    const leadAppNumber = appNum || extractApplicationNumberFromResponse(result);
    const leadCaseId = extractCaseIdFromResponse(result);
    if (leadAppNumber) {
      dispatch(journeyActions.setApplicationNumber(leadAppNumber));
      setStoredApplicationNumber(leadAppNumber);
    }
    if (leadCaseId) {
      dispatch(journeyActions.setCaseId(leadCaseId));
      setStoredCaseId(leadCaseId);
    }
    const assignedName = result?.assignedProductName || '';
    const isEliteEligible = getIsEliteEligibleForPremium(pincodeAvailArg, assignedName);
    const bulkPlanOnLoad = resolveBulkPlanOnQuoteEntry(pincodeAvailArg, assignedName);
    const defaultCover = getDefaultCoverAmount(result?.maxCoverAmount);
    const attainedAgeOnLead = getAttainedAgeFromLead(result, formData);
    const rawPT = result?.policyTermOptions;
    const ptScalar = Number((Array.isArray(rawPT) ? rawPT[0] : rawPT) ?? 30);
    const maxPtForPlan = effectiveMaxPolicyTermForPlan(
      rawPT,
      bulkPlanOnLoad,
      attainedAgeOnLead,
    );
    const policyTermFromLead =
      maxPtForPlan != null ? Math.min(ptScalar, maxPtForPlan) : ptScalar;
    const riderOverrides = buildRiderOverridesFromSelection([], {});
    const ropEcboDefault = buildRopEcboOverrides(
      { premiumRefundEnabled: false, enhancedCoverageEnabled: false },
      isEliteEligible,
      bulkPlanOnLoad,
    );
    const overrides = buildJourneySpecificPremiumOverrides(
      { baseSumAssured: defaultCover, policyTerm: policyTermFromLead, ...ropEcboDefault, ...riderOverrides },
      {
        plan: bulkPlanOnLoad,
        premiumFrequency: 'Yearly',
        policyTerm: policyTermFromLead,
        selectedLimitedPayTerm: null,
        leadFormDataOverride: formData || {},
      },
    );

    try {
      const productCode = resolveNischitProductCode(formData || {});
      const jointBulkOnEntry =
        bulkPlanOnLoad === PLAN_NAMES_NISCHIT_PENSION.PLAN_B &&
        isNischitSecondaryAnnuitantComplete(formData || {});
      const planIds = getNischitVisiblePlanOptionIds(productCode, jointBulkOnEntry);
      const primaryPlanKey = jointBulkOnEntry
        ? bulkPlanOnLoad
        : PLAN_NAMES_NISCHIT_PENSION.PLAN_A;
      const premiumLead = {
        ...(result || {}),
        leadInput: {
          ...((result && result.leadInput) || {}),
          ...(formData || {}),
        },
      };
      const premiumPromise = calculateNishchitBulkPremiumFromLead(
        premiumLead,
        stripNischitPlanOptionFromOverrides(overrides),
        planIds,
      ).then((map) => {
        const primaryId = resolveNischitPlanOptionId({}, primaryPlanKey, productCode);
        return map && typeof map === 'object' ? (map[primaryId] || null) : null;
      });
      const [quoteOutcome, premiumOutcome] = await Promise.allSettled([
        initQuote(formData || {}, leadAppNumber, leadCaseId),
        premiumPromise,
      ]);

      if (quoteOutcome.status === 'fulfilled') {
        const quoteResponse = quoteOutcome.value || null;
        dispatch(journeyActions.setQuoteInitResult(quoteResponse));
        const initAppNumber = extractApplicationNumberFromResponse(quoteResponse);
        const initCaseId = extractCaseIdFromResponse(quoteResponse);
        const appNumberToSet =
          leadAppNumber && initAppNumber && String(leadAppNumber).trim() !== String(initAppNumber).trim()
            ? leadAppNumber
            : (initAppNumber || leadAppNumber);
        if (appNumberToSet) {
          dispatch(journeyActions.setApplicationNumber(appNumberToSet));
          setStoredApplicationNumber(appNumberToSet);
        }
        if (initCaseId) {
          dispatch(journeyActions.setCaseId(initCaseId));
          setStoredCaseId(initCaseId);
        }
      }

      if (premiumOutcome.status === 'fulfilled') {
        dispatch(journeyActions.setPremiumResult(premiumOutcome.value || null));
        dispatch(journeyActions.setMultiDimensionPremium(null));
        dispatch(journeyActions.setMultiDimensionByPlan(null));
      } else if (!isSupersededPremiumRequestError(premiumOutcome.reason)) {
        dispatch(journeyActions.setPremiumResult(null));
        dispatch(journeyActions.setMultiDimensionPremium(null));
        dispatch(journeyActions.setMultiDimensionByPlan(null));
        dispatch(journeyActions.setPremiumError(premiumOutcome.reason));
      }
    } finally {
      dispatch(journeyActions.endPremiumLoading());
    }
  }, [dispatch, navigate]);

  const handleBackToLead = useCallback(() => {
    dispatch(journeyActions.navigateTo('lead'));
    const routePath = getCurrentJourneyContext()?.routePath || '/retirement-plan';
    navigate(routePath);
  }, [dispatch, navigate]);

  const handleBackToQuote = useCallback(() => {
    dispatch(journeyActions.navigateTo('quote'));
    // Allow Pay/submit on quote even though backend stage is already QuoteSummary.
    dispatch(journeyActions.setAllowQuoteResubmit(true));
    navigate('/quote');

    // Refresh from backend so Quote reflects the latest saved journey state
    // (e.g. cover/plan edits made on a different tab/session) instead of whatever
    // was last held in memory before the user drilled into Summary.
    const appNumber = state.applicationNumber || getStoredApplicationNumber();
    if (!appNumber) return;
    const dob = state.leadFormData?.dateOfBirth || state.leadFormData?.lifeAssuredDob || null;
    getJourneyData(appNumber, dob)
      .then((journey) => {
        if (journey && typeof journey === 'object') {
          hydrateStoreFromJourney(dispatch, journey, appNumber, () => ({ journey: state }));
          // hydrateStoreFromJourney sets stage from backend resume route (often summary);
          // keep the user on quote and preserve the one-shot resubmit bypass.
          dispatch(journeyActions.navigateTo('quote'));
          dispatch(journeyActions.setAllowQuoteResubmit(true));
        }
      })
      .catch((e) => {
        logError('[Journey] Failed to refresh journey data on back-to-quote', e);
      });
  }, [dispatch, navigate, state]);

  const handleQuoteComplete = useCallback(({ result, quoteData }) => {
    dispatch(journeyActions.quoteComplete({ ...result, ...quoteData }));
    navigate('/summary');
  }, [dispatch, navigate]);

  const resolvePremiumForDimensions = useCallback((multiResult, premiumFrequency, paymentTermType, selectedLimitedPayTerm) => {
    const termType = paymentTermType === 'Limited Pay' ? 'LP' : 'RP';
    const ppt = paymentTermType === 'Limited Pay' ? selectedLimitedPayTerm : null;
    return getPremiumResultForDimensions(
      multiResult,
      premiumFrequency || multiResult?.primaryPremiumPaymentMode || 'Yearly',
      termType,
      ppt ?? 0,
    );
  }, []);

  const recalcNischitPremium = useCallback(async (
    plan,
    coverAmount,
    policyTerm,
    premiumFrequency,
    paymentTermType,
    selectedLimitedPayTerm,
    selectedRiderIds,
    riderCovers,
    leadFormDataOverride,
  ) => {
    const leadResult = mergeLeadAndQuoteInitForPremium(state.leadResult, state.quoteInitResult);
    if (!leadResult) return true;
    if (
      plan === PLAN_NAMES_NISCHIT_PENSION.PLAN_B &&
      !isNischitSecondaryAnnuitantComplete(state?.customizePlanData || {})
    ) {
      return true;
    }
    const riderOverrides = buildRiderOverridesFromSelection(
      selectedRiderIds ?? state.quoteRiders?.selectedIds ?? [],
      riderCovers ?? state.quoteRiders?.covers ?? {},
      policyTerm,
      paymentTermType,
      selectedLimitedPayTerm,
    );
    const isEliteEligible = getIsEliteEligibleForPremium(
      state.pincodeTermPlanAvailability,
      leadResult?.assignedProductName,
    );
    const ropEcbo = buildRopEcboOverrides(state.quoteOptions, isEliteEligible, plan);
    const overrides = buildJourneySpecificPremiumOverrides(
      {
        baseSumAssured: coverAmount,
        policyTerm,
        ...ropEcbo,
        ...riderOverrides,
      },
      {
        plan,
        premiumFrequency,
        policyTerm,
        selectedLimitedPayTerm,
        leadFormDataOverride,
      },
    );
    try {
      dispatch(journeyActions.setPremiumError(null));
      const productCode = resolveNischitProductCode(state?.leadFormData || {});
      const isJointMain = plan === PLAN_NAMES_NISCHIT_PENSION.PLAN_B;
      const planOptionIds = getNischitVisiblePlanOptionIds(productCode, isJointMain);
      const planOptionId = resolveNischitPlanOptionId(
        state?.customizePlanData || {},
        plan,
        productCode,
      );
      const result = await runWithPremiumLoading(() =>
        calculateNishchitBulkPremiumFromLead(
          leadResult,
          stripNischitPlanOptionFromOverrides(overrides),
          planOptionIds,
        ).then((m) => m[planOptionId] || null),
      );
      dispatch(journeyActions.setPremiumResult(result || null));
      dispatch(journeyActions.setMultiDimensionPremium(null));
      dispatch(journeyActions.setMultiDimensionByPlan(null));
    } catch (e) {
      if (!isSupersededPremiumRequestError(e)) {
        dispatch(journeyActions.setPremiumResult(null));
        dispatch(journeyActions.setMultiDimensionPremium(null));
        dispatch(journeyActions.setMultiDimensionByPlan(null));
        dispatch(journeyActions.setPremiumError(e));
      }
    }
    return true;
  }, [
    dispatch,
    state.leadResult,
    state.quoteInitResult,
    state.quoteRiders,
    state.quoteOptions,
    state.pincodeTermPlanAvailability,
    state.leadFormData,
    buildJourneySpecificPremiumOverrides,
    runWithPremiumLoading,
    state.customizePlanData,
  ]);

  const calculateNischitPremiumForPlanOption = useCallback(async ({
    plan,
    coverAmount,
    policyTerm,
    premiumFrequency,
    paymentTermType,
    selectedLimitedPayTerm,
    planOption,
  } = {}) => {
    const leadResult = mergeLeadAndQuoteInitForPremium(state.leadResult, state.quoteInitResult);
    if (!leadResult) return null;
    const riderOverrides = buildRiderOverridesFromSelection(
      state.quoteRiders?.selectedIds ?? [],
      state.quoteRiders?.covers ?? {},
      policyTerm,
      paymentTermType,
      selectedLimitedPayTerm,
    );
    const isEliteEligible = getIsEliteEligibleForPremium(
      state.pincodeTermPlanAvailability,
      leadResult?.assignedProductName,
    );
    const ropEcbo = buildRopEcboOverrides(state.quoteOptions, isEliteEligible, plan);
    const overrides = buildJourneySpecificPremiumOverrides(
      {
        baseSumAssured: coverAmount,
        policyTerm,
        ...ropEcbo,
        ...riderOverrides,
      },
      { plan, premiumFrequency, policyTerm, selectedLimitedPayTerm },
    );
    const productCode = resolveNischitProductCode(state?.leadFormData || {});
    const op = String(
      planOption ??
      overrides?.additionalInputFields?.planOption ??
      resolveNischitPlanOptionId(state?.customizePlanData || {}, plan, productCode),
    );
    const jointIds = getNischitVisiblePlanOptionIds(productCode, true);
    const isJointOp = jointIds.includes(op);
    if (isJointOp && !isNischitSecondaryAnnuitantComplete(state?.customizePlanData || {})) {
      return Promise.resolve(null);
    }
    const planIds = isJointOp
      ? jointIds
      : getNischitVisiblePlanOptionIds(productCode, false);
    return calculateNishchitBulkPremiumFromLead(
      leadResult,
      stripNischitPlanOptionFromOverrides(overrides),
      planIds,
    ).then((m) => m[op] || null);
  }, [
    state.leadResult,
    state.quoteInitResult,
    state.quoteRiders,
    state.quoteOptions,
    state.pincodeTermPlanAvailability,
    state.leadFormData,
    state.customizePlanData,
    buildJourneySpecificPremiumOverrides,
  ]);

  /** Prefetch all visible Nishchit plan rows in one bulk API call (same base payload as calculate). */
  const calculateNischitPremiumsBulkForPlanOptions = useCallback(
    async ({
      plan,
      coverAmount,
      policyTerm,
      premiumFrequency,
      paymentTermType,
      selectedLimitedPayTerm,
      planOptionIds = [],
    } = {}) => {
      const leadResult = mergeLeadAndQuoteInitForPremium(state.leadResult, state.quoteInitResult);
      if (!leadResult || !Array.isArray(planOptionIds) || planOptionIds.length === 0) {
        return {};
      }
      const riderOverrides = buildRiderOverridesFromSelection(
        state.quoteRiders?.selectedIds ?? [],
        state.quoteRiders?.covers ?? {},
        policyTerm,
        paymentTermType,
        selectedLimitedPayTerm,
      );
      const isEliteEligible = getIsEliteEligibleForPremium(
        state.pincodeTermPlanAvailability,
        leadResult?.assignedProductName,
      );
      const ropEcbo = buildRopEcboOverrides(state.quoteOptions, isEliteEligible, plan);
      const overrides = buildJourneySpecificPremiumOverrides(
        {
          baseSumAssured: coverAmount,
          policyTerm,
          ...ropEcbo,
          ...riderOverrides,
        },
        { plan, premiumFrequency, policyTerm, selectedLimitedPayTerm },
      );
      return calculateNishchitBulkPremiumFromLead(
        leadResult,
        stripNischitPlanOptionFromOverrides(overrides),
        planOptionIds,
      );
    },
    [
      state.leadResult,
      state.quoteInitResult,
      state.quoteRiders,
      state.quoteOptions,
      state.pincodeTermPlanAvailability,
      buildJourneySpecificPremiumOverrides,
    ],
  );

  const handlePremiumModeChange = useCallback((newMode, paymentTermType, selectedLimitedPayTerm) => {
    if (!state.multiDimensionPremiumResult) return;
    const result = resolvePremiumForDimensions(
      state.multiDimensionPremiumResult,
      newMode,
      paymentTermType ?? 'Regular Pay',
      selectedLimitedPayTerm ?? null,
    );
    if (result) {
      dispatch(journeyActions.setPremiumResult(result));
    }
  }, [dispatch, state.multiDimensionPremiumResult, resolvePremiumForDimensions]);

  const handlePaymentTermChange = useCallback((paymentTermType, premiumPayingTerm, premiumFrequency) => {
    if (!state.multiDimensionPremiumResult) return;
    const result = resolvePremiumForDimensions(
      state.multiDimensionPremiumResult,
      premiumFrequency || state.multiDimensionPremiumResult?.primaryPremiumPaymentMode || 'Yearly',
      paymentTermType ?? 'Regular Pay',
      premiumPayingTerm ?? null,
    );
    if (result) {
      dispatch(journeyActions.setPremiumResult(result));
    }
  }, [dispatch, state.multiDimensionPremiumResult, resolvePremiumForDimensions]);

  /**
   * Premium payment frequency change: one bulk calculate for the selected frequency (all SA×PT×LP/RP in grid).
   * Skips the network if the plan cache already holds that frequency (e.g. switching back to a previously fetched mode).
   * Optional `selectedRiderIds` / `riderCovers` align bulk payloads with Quote UI when Redux lags local state.
   */
  const handlePremiumFrequencyChange = useCallback(async (
    newFrequency, plan, coverAmount, policyTerm, paymentTermType, selectedLimitedPayTerm,
    selectedRiderIds = undefined,
    riderCovers = undefined,
  ) => {
    if (!newFrequency || !plan || coverAmount == null) return;
    if (await recalcNischitPremium(
      plan,
      coverAmount,
      policyTerm,
      newFrequency,
      paymentTermType,
      selectedLimitedPayTerm,
    )) return;
    const leadResult = state.leadResult || state.quoteInitResult;
    if (!leadResult) return;
    const { pt, attainedAge } = resolveActivePolicyTermForBulk(
      leadResult,
      plan,
      policyTerm,
    );

    const cornerMulti = getCachedMulti(state.multiDimensionByPlan, plan, coverAmount, pt);
    if (cornerMulti?.primaryPremiumPaymentMode === newFrequency) {
      dispatch(journeyActions.setPremiumError(null));
      dispatch(journeyActions.setMultiDimensionPremium(cornerMulti));
      const r = resolvePremiumForDimensions(
        cornerMulti,
        newFrequency,
        paymentTermType ?? 'Regular Pay',
        selectedLimitedPayTerm ?? null,
      );
      if (r) dispatch(journeyActions.setPremiumResult(r));
      return;
    }

    const resolvedRiderIds = selectedRiderIds !== undefined
      ? selectedRiderIds
      : state.quoteRiders?.selectedIds ?? [];
    const resolvedRiderCovers = riderCovers !== undefined
      ? riderCovers
      : state.quoteRiders?.covers ?? {};
    const riderOverrides = buildRiderOverridesFromSelection(
      resolvedRiderIds,
      resolvedRiderCovers,
      pt, paymentTermType, selectedLimitedPayTerm,
    );
    const isEliteEligible = getIsEliteEligibleForPremium(
      state.pincodeTermPlanAvailability,
      leadResult?.assignedProductName,
    );
    const ropEcbo = buildRopEcboOverrides(state.quoteOptions, isEliteEligible, plan);
    const overrides = buildJourneySpecificPremiumOverrides({
      baseSumAssured: coverAmount,
      policyTerm: pt,
      ...ropEcbo,
      ...riderOverrides,
    }, { plan, premiumFrequency: newFrequency, policyTerm: pt, selectedLimitedPayTerm });

    const coverAmounts = generateCoverAmountValues(leadResult?.maxCoverAmount, plan);
    const policyTerms = resolvePolicyTermsForBulk(
      leadResult?.policyTermOptions,
      pt,
      plan,
      attainedAge,
    );
    const baseGrid = buildAllVariations(
      coverAmounts.length ? coverAmounts : [coverAmount],
      policyTerms,
    );
    // Keep frequency-change cache coherent for desired/custom inputs too.
    const desiredCoverExtras = buildAllVariations([Number(coverAmount)], policyTerms);
    const coverList = [...new Set([...(coverAmounts.length ? coverAmounts : [coverAmount]).map(Number), Number(coverAmount)])]
      .filter((n) => Number.isFinite(n) && n > 0);
    const desiredPtExtras = buildAllVariations(coverList, [Number(pt)]);
    const variations = mergeVariationSets([baseGrid, desiredCoverExtras, desiredPtExtras]);

    try {
      dispatch(journeyActions.setPremiumError(null));
      const bulkResult = await runWithPremiumLoading(() => fetchBulkForPlan(
        plan, leadResult, overrides, variations,
        newFrequency, paymentTermType, selectedLimitedPayTerm, coverAmount, pt,
      ));
      const primaryKey = `${coverAmount}_${pt}`;
      const newMulti = bulkResult?.results?.[primaryKey] || null;
      dispatch(journeyActions.setMultiDimensionPremium(newMulti));
      const resultForDimensions = resolvePremiumForDimensions(
        newMulti,
        newFrequency,
        paymentTermType ?? 'Regular Pay',
        selectedLimitedPayTerm ?? null,
      );
      if (resultForDimensions) dispatch(journeyActions.setPremiumResult(resultForDimensions));
    } catch (e) {
      if (!isSupersededPremiumRequestError(e)) {
        dispatch(journeyActions.setPremiumError(e));
      }
    }
  }, [dispatch, recalcNischitPremium, state.multiDimensionByPlan, state.quoteInitResult, state.leadResult, state.pincodeTermPlanAvailability, state.quoteRiders, state.quoteOptions, resolvePremiumForDimensions, runWithPremiumLoading, fetchBulkForPlan]);

  /** Plan switch: try 3-level cache first, else bulk re-fetch for the new plan. */
  const handlePlanChange = useCallback(async (plan, premiumFrequency, coverAmount, paymentTermType, selectedLimitedPayTerm, policyTerm) => {
    if (await recalcNischitPremium(
      plan,
      coverAmount,
      policyTerm,
      premiumFrequency,
      paymentTermType,
      selectedLimitedPayTerm,
    )) return;
    const byPlan = state.multiDimensionByPlan;
    const leadResult = state.leadResult || state.quoteInitResult;
    if (!leadResult) return;
    const { pt, attainedAge } = resolveActivePolicyTermForBulk(
      leadResult,
      plan,
      policyTerm,
    );

    const multiResult = getCachedMulti(byPlan, plan, coverAmount, pt);
    if (multiResult) {
      dispatch(journeyActions.setPremiumError(null));
      dispatch(journeyActions.setMultiDimensionPremium(multiResult));
      const resultForDimensions = resolvePremiumForDimensions(multiResult, premiumFrequency, paymentTermType ?? 'Regular Pay', selectedLimitedPayTerm);
      if (resultForDimensions) dispatch(journeyActions.setPremiumResult(resultForDimensions));
      return;
    }

    if (coverAmount == null) return;
    const riderOverrides = buildRiderOverridesFromSelection(
      state.quoteRiders?.selectedIds ?? [],
      state.quoteRiders?.covers ?? {},
      pt, paymentTermType, selectedLimitedPayTerm,
    );
    const isEliteEligible = getIsEliteEligibleForPremium(
      state.pincodeTermPlanAvailability,
      leadResult?.assignedProductName,
    );
    const ropEcbo = buildRopEcboOverrides(state.quoteOptions, isEliteEligible, plan);
    const overrides = buildJourneySpecificPremiumOverrides({
      baseSumAssured: coverAmount,
      policyTerm: pt,
      ...ropEcbo,
      ...riderOverrides,
    }, { plan, premiumFrequency, policyTerm: pt, selectedLimitedPayTerm });

    const coverAmounts = generateCoverAmountValues(leadResult?.maxCoverAmount, plan);
    const policyTerms = resolvePolicyTermsForBulk(
      leadResult?.policyTermOptions,
      pt,
      plan,
      attainedAge,
    );
    const variations = buildAllVariations(
      coverAmounts.length ? coverAmounts : [coverAmount],
      policyTerms,
    );

    try {
      dispatch(journeyActions.setPremiumError(null));
      const bulkResult = await runWithPremiumLoading(() => fetchBulkForPlan(
        plan, leadResult, overrides, variations,
        premiumFrequency, paymentTermType, selectedLimitedPayTerm, coverAmount, pt,
      ));
      const primaryKey = `${coverAmount}_${pt}`;
      const newMulti = bulkResult?.results?.[primaryKey] || null;
      dispatch(journeyActions.setMultiDimensionPremium(newMulti));
      const resultForDimensions = resolvePremiumForDimensions(newMulti, premiumFrequency, paymentTermType ?? 'Regular Pay', selectedLimitedPayTerm);
      if (resultForDimensions) dispatch(journeyActions.setPremiumResult(resultForDimensions));
    } catch (e) {
      if (!isSupersededPremiumRequestError(e)) {
        dispatch(journeyActions.setPremiumError(e));
      }
    }
  }, [dispatch, recalcNischitPremium, state.multiDimensionByPlan, state.quoteInitResult, state.leadResult, state.pincodeTermPlanAvailability, state.quoteRiders, state.quoteOptions, resolvePremiumForDimensions, runWithPremiumLoading, fetchBulkForPlan]);

  /** Cover amount change: pure cache lookup from the 3-level cache. Falls back to single API call for custom values not in cache. */
  const handleCoverAmountChange = useCallback(async (plan, coverAmount, premiumFrequency, paymentTermType, selectedLimitedPayTerm, policyTerm) => {
    if (coverAmount == null) return;
    if (await recalcNischitPremium(
      plan,
      coverAmount,
      policyTerm,
      premiumFrequency,
      paymentTermType,
      selectedLimitedPayTerm,
    )) return;
    const byPlan = state.multiDimensionByPlan;
    const leadResult = state.leadResult || state.quoteInitResult;
    if (!leadResult) return;
    const { pt, attainedAge } = resolveActivePolicyTermForBulk(
      leadResult,
      plan,
      policyTerm,
    );

    const multiResult = getCachedMulti(byPlan, plan, coverAmount, pt);
    if (multiResult) {
      dispatch(journeyActions.setPremiumError(null));
      dispatch(journeyActions.setMultiDimensionPremium(multiResult));
      const resultForDimensions = resolvePremiumForDimensions(multiResult, premiumFrequency, paymentTermType ?? 'Regular Pay', selectedLimitedPayTerm);
      if (resultForDimensions) dispatch(journeyActions.setPremiumResult(resultForDimensions));
      return;
    }

    const riderOverrides = buildRiderOverridesFromSelection(
      state.quoteRiders?.selectedIds ?? [], state.quoteRiders?.covers ?? {},
      pt, paymentTermType, selectedLimitedPayTerm,
    );
    const isEliteEligible = getIsEliteEligibleForPremium(
      state.pincodeTermPlanAvailability,
      leadResult?.assignedProductName,
    );
    const ropEcbo = buildRopEcboOverrides(state.quoteOptions, isEliteEligible, plan);
    const overrides = buildJourneySpecificPremiumOverrides({
      baseSumAssured: coverAmount,
      policyTerm: pt,
      ...ropEcbo,
      ...riderOverrides,
    }, { plan, premiumFrequency, policyTerm: pt, selectedLimitedPayTerm });

    const gridCovers = generateCoverAmountValues(leadResult?.maxCoverAmount, plan);
    const policyTerms = resolvePolicyTermsForBulk(
      leadResult?.policyTermOptions,
      pt,
      plan,
      attainedAge,
    );
    const baseGrid = buildAllVariations(
      gridCovers.length ? gridCovers : [coverAmount],
      policyTerms,
    );
    // Desired (custom) life cover: same SA across every policy term on the quote, merged with full grid.
    const desiredCoverExtras = buildAllVariations([Number(coverAmount)], policyTerms);
    const variations = mergeVariationSets([baseGrid, desiredCoverExtras]);

    try {
      dispatch(journeyActions.setPremiumError(null));
      const bulkResult = await runWithPremiumLoading(() => fetchBulkForPlan(
        plan, leadResult, overrides, variations,
        premiumFrequency, paymentTermType, selectedLimitedPayTerm, coverAmount, pt,
      ));
      const primaryKey = `${coverAmount}_${pt}`;
      const result = bulkResult?.results?.[primaryKey] || null;
      dispatch(journeyActions.setMultiDimensionPremium(result));
      const resultForDimensions = resolvePremiumForDimensions(result, premiumFrequency, paymentTermType ?? 'Regular Pay', selectedLimitedPayTerm);
      if (resultForDimensions) dispatch(journeyActions.setPremiumResult(resultForDimensions));
    } catch (e) {
      if (!isSupersededPremiumRequestError(e)) {
        dispatch(journeyActions.setPremiumError(e));
      }
    }
  }, [dispatch, recalcNischitPremium, state.multiDimensionByPlan, state.quoteInitResult, state.leadResult, state.pincodeTermPlanAvailability, state.quoteRiders, state.quoteOptions, resolvePremiumForDimensions, runWithPremiumLoading, fetchBulkForPlan]);

  /** Policy term / cover-till-age change: pure cache lookup from the 3-level cache. Falls back to single API call if not found. */
  const handleQuoteDimensionsChange = useCallback(async (plan, coverAmount, policyTerm, premiumFrequency, paymentTermType, selectedLimitedPayTerm, leadFormDataOverride) => {
    if (coverAmount == null) return;
    if (await recalcNischitPremium(
      plan,
      coverAmount,
      policyTerm,
      premiumFrequency,
      paymentTermType,
      selectedLimitedPayTerm,
      null,
      null,
      leadFormDataOverride,
    )) return;
    const byPlan = state.multiDimensionByPlan;
    const leadResult = state.leadResult || state.quoteInitResult;
    if (!leadResult) return;
    const { pt, attainedAge } = resolveActivePolicyTermForBulk(
      leadResult,
      plan,
      policyTerm,
    );

    const multiResult = getCachedMulti(byPlan, plan, coverAmount, pt);
    if (multiResult) {
      dispatch(journeyActions.setPremiumError(null));
      dispatch(journeyActions.setMultiDimensionPremium(multiResult));
      const resultForDimensions = resolvePremiumForDimensions(multiResult, premiumFrequency, paymentTermType ?? 'Regular Pay', selectedLimitedPayTerm);
      if (resultForDimensions) dispatch(journeyActions.setPremiumResult(resultForDimensions));
      return;
    }

    const productCode = PLAN_PRODUCT_CODES_NISCHIT_PENSION[plan] || leadResult?.assignedProductCode;
    if (!productCode) return;
    const riderOverrides = buildRiderOverridesFromSelection(
      state.quoteRiders?.selectedIds ?? [], state.quoteRiders?.covers ?? {},
      pt, paymentTermType, selectedLimitedPayTerm,
    );
    const isEliteEligible = getIsEliteEligibleForPremium(
      state.pincodeTermPlanAvailability,
      leadResult?.assignedProductName,
    );
    const ropEcbo = buildRopEcboOverrides(state.quoteOptions, isEliteEligible, plan);
    const overrides = buildJourneySpecificPremiumOverrides({
      baseSumAssured: coverAmount,
      policyTerm: pt,
      ...ropEcbo,
      ...riderOverrides,
    }, { plan, premiumFrequency, policyTerm: pt, selectedLimitedPayTerm });

    const gridCovers = generateCoverAmountValues(leadResult?.maxCoverAmount, plan);
    const policyTerms = resolvePolicyTermsForBulk(
      leadResult?.policyTermOptions,
      pt,
      plan,
      attainedAge,
    );
    const baseGrid = buildAllVariations(
      gridCovers.length ? gridCovers : [coverAmount],
      policyTerms,
    );
    // Desired policy term / cover-till-age: every grid (and current) life cover at this PT, merged with full grid.
    const coverList = [...new Set([...(gridCovers.length ? gridCovers : [coverAmount]).map(Number), Number(coverAmount)])]
      .filter((n) => Number.isFinite(n) && n > 0);
    const desiredPtExtras = buildAllVariations(coverList, [Number(pt)]);
    const variations = mergeVariationSets([baseGrid, desiredPtExtras]);

    try {
      dispatch(journeyActions.setPremiumError(null));
      const bulkResult = await runWithPremiumLoading(() => fetchBulkForPlan(
        plan, leadResult, overrides, variations,
        premiumFrequency, paymentTermType, selectedLimitedPayTerm, coverAmount, pt,
      ));
      const primaryKey = `${coverAmount}_${pt}`;
      const result = bulkResult?.results?.[primaryKey] || null;
      dispatch(journeyActions.setMultiDimensionPremium(result));
      const resultForDimensions = resolvePremiumForDimensions(result, premiumFrequency, paymentTermType ?? 'Regular Pay', selectedLimitedPayTerm);
      if (resultForDimensions) dispatch(journeyActions.setPremiumResult(resultForDimensions));
    } catch (e) {
      if (!isSupersededPremiumRequestError(e)) {
        dispatch(journeyActions.setPremiumError(e));
      }
    }
  }, [dispatch, recalcNischitPremium, state.multiDimensionByPlan, state.quoteInitResult, state.leadResult, state.pincodeTermPlanAvailability, state.quoteRiders, state.quoteOptions, resolvePremiumForDimensions, runWithPremiumLoading, fetchBulkForPlan]);

  /** Rider / ROP / ECBO change: invalidate cache and bulk re-fetch for the current plan with all variations. */
  const handleRiderChange = useCallback(async (plan, coverAmount, policyTerm, premiumFrequency, paymentTermType, selectedLimitedPayTerm, selectedRiderIds, riderCovers) => {
    if (coverAmount == null) return;
    if (await recalcNischitPremium(
      plan,
      coverAmount,
      policyTerm,
      premiumFrequency,
      paymentTermType,
      selectedLimitedPayTerm,
      selectedRiderIds,
      riderCovers,
    )) return;
    const leadResult = state.leadResult || state.quoteInitResult;
    if (!leadResult) return;
    const productCode = PLAN_PRODUCT_CODES_NISCHIT_PENSION[plan] || leadResult?.assignedProductCode;
    if (!productCode) return;
    dispatch(journeyActions.setQuoteRiders({ selectedIds: selectedRiderIds ?? [], covers: riderCovers ?? {} }));
    const { pt, attainedAge } = resolveActivePolicyTermForBulk(
      leadResult,
      plan,
      policyTerm,
    );
    const riderOverrides = buildRiderOverridesFromSelection(
      selectedRiderIds ?? [], riderCovers ?? {},
      pt, paymentTermType, selectedLimitedPayTerm,
    );
    const isEliteEligible = getIsEliteEligibleForPremium(
      state.pincodeTermPlanAvailability,
      leadResult?.assignedProductName,
    );
    const ropEcbo = buildRopEcboOverrides(state.quoteOptions, isEliteEligible, plan);
    const overrides = buildJourneySpecificPremiumOverrides({
      baseSumAssured: coverAmount,
      policyTerm: pt,
      ...ropEcbo,
      ...riderOverrides,
    }, { plan, premiumFrequency, policyTerm: pt, selectedLimitedPayTerm });

    const coverAmounts = generateCoverAmountValues(leadResult?.maxCoverAmount, plan);
    const policyTerms = resolvePolicyTermsForBulk(
      leadResult?.policyTermOptions,
      pt,
      plan,
      attainedAge,
    );
    const variations = buildAllVariations(
      coverAmounts.length ? coverAmounts : [coverAmount],
      policyTerms,
    );

    try {
      dispatch(journeyActions.setPremiumError(null));
      const bulkResult = await runWithPremiumLoading(() => fetchBulkForPlan(
        plan, leadResult, overrides, variations,
        premiumFrequency, paymentTermType, selectedLimitedPayTerm, coverAmount, pt,
      ));
      const primaryKey = `${coverAmount}_${pt}`;
      const newMulti = bulkResult?.results?.[primaryKey] || null;
      dispatch(journeyActions.setMultiDimensionPremium(newMulti));
      const resultForDimensions = resolvePremiumForDimensions(newMulti, premiumFrequency, paymentTermType ?? 'Regular Pay', selectedLimitedPayTerm);
      if (resultForDimensions) dispatch(journeyActions.setPremiumResult(resultForDimensions));
    } catch (e) {
      if (!isSupersededPremiumRequestError(e)) {
        dispatch(journeyActions.setPremiumError(e));
      }
    }
  }, [dispatch, recalcNischitPremium, state.quoteInitResult, state.leadResult, state.pincodeTermPlanAvailability, state.quoteOptions, resolvePremiumForDimensions, runWithPremiumLoading, fetchBulkForPlan]);

  const handlePayment = useCallback(async (paymentData) => {
    const flowMode = paymentData?.paymentFlowMode || PAYMENT_FLOW.PAY_FIRST;
    dispatch(journeyActions.setPaymentFlowMode(flowMode));
    const {
      applicationNumber,
      customerName: paymentCustomerName,
      premiumAmount,
      mobileNumber,
      emailAddress,
      productId,
      productCode,
      bisOpsProductCode,
    } = paymentData || {};

    const appNum = applicationNumber || state?.applicationNumber;
    const mobile = mobileNumber || state?.leadFormData?.proposerMobileNumber || state?.leadFormData?.mobileNumber;
    const email = emailAddress || state?.leadFormData?.proposerEmailAddress || state?.leadFormData?.emailAddress;

    const leadInput = state.leadResult?.leadInput || state.leadResult || {};
    const leadFormData = state.leadFormData || {};
    const nameFromLead =
      leadInput.lifeAssuredName ||
      leadInput.proposerName ||
      leadInput.fullName ||
      leadInput.name ||
      leadFormData.proposerName ||
      leadFormData.fullName ||
      leadFormData.name ||
      (leadFormData.firstName && [leadFormData.firstName, leadFormData.middleName, leadFormData.lastName].filter(Boolean).join(' '));
    const customerName =
      (paymentCustomerName && paymentCustomerName.trim() !== '' && paymentCustomerName !== 'Customer')
        ? paymentCustomerName
        : (nameFromLead && nameFromLead.trim() !== '' ? nameFromLead : 'Customer');

    if (!mobile) throw new Error('Mobile number is required for payment.');
    if (!email) throw new Error('Email address is required for payment.');
    if (!premiumAmount) throw new Error('Premium amount is required for payment.');
    const hasProduct = productId !== undefined && productId !== null && String(productId).trim() !== '';
    const hasCode = (productCode !== undefined && productCode !== null && String(productCode).trim() !== '')
      || (bisOpsProductCode !== undefined && bisOpsProductCode !== null && String(bisOpsProductCode).trim() !== '');
    if (!hasProduct && !hasCode) throw new Error('Product ID, productCode, or bisOpsProductCode is required for payment.');

    const paymentRequest = buildPaymentRequest({
      applicationNumber: appNum,
      customerName,
      premiumAmount,
      mobileNumber: mobile,
      emailAddress: email,
      productId,
      productCode,
      bisOpsProductCode,
      currentStage: state.currentStage,
    });
    saveJourneySnapshot({
      ...state,
      paymentFlowMode: flowMode,
    });
    const result = await initiatePayment(paymentRequest);
    if (!result.success) {
      throw new Error(result.errorMessage || 'Failed to initiate payment.');
    }
    if (result.alreadyPaid) {
      dispatch(journeyActions.setPaymentTransactionStatus('SUCCESS'));
      if (flowMode === PAYMENT_FLOW.PAY_LAST) {
        return { success: true, alreadyPaid: true };
      }
      navigate('/verification');
      return { success: true, alreadyPaid: true };
    }
    if (result.paymentAwaiting) {
      dispatch(journeyActions.setPaymentTransactionStatus(result.transactionStatus || 'AWAITING_RESPONSE'));
      return {
        success: true,
        paymentAwaiting: true,
        message:
          result.message ||
          "We're verifying your payment. If this takes a bit longer, you can still continue via 'Retrieve Application'",
      };
    }
  }, [dispatch, navigate, state]);

  const handleSkipPayment = useCallback(() => {
    dispatch(journeyActions.setPaymentFlowMode(PAYMENT_FLOW.PAY_LAST));
    dispatch(journeyActions.skipPayment());
    navigate('/verification');
  }, [dispatch, navigate]);

  const handleCustomerVerificationComplete = useCallback((verificationData) => {
    dispatch(journeyActions.setCustomerVerificationData(verificationData));
  }, [dispatch]);

  const saveStepData = useCallback((step, data) => {
    dispatch(journeyActions.saveStepData({ step, data }));
  }, [dispatch]);

  const effectiveLeadResult = state.quoteInitResult || state.leadResult;

  return useMemo(() => ({
    ...state,
    effectiveLeadResult,
    dispatch,
    handleLeadComplete,
    handleBackToLead,
    handleBackToQuote,
    handleQuoteComplete,
    handlePremiumModeChange,
    handlePremiumFrequencyChange,
    handlePaymentTermChange,
    handlePlanChange,
    handleCoverAmountChange,
    handleQuoteDimensionsChange,
    handleRiderChange,
    calculateNischitPremiumForPlanOption,
    calculateNischitPremiumsBulkForPlanOptions,
    handlePayment,
    handleSkipPayment,
    handleCustomerVerificationComplete,
    saveStepData,
  }), [
    state, effectiveLeadResult, dispatch,
    handleLeadComplete, handleBackToLead, handleBackToQuote,
    handleQuoteComplete, handlePremiumModeChange, handlePremiumFrequencyChange, handlePaymentTermChange, handlePlanChange, handleCoverAmountChange, handleQuoteDimensionsChange, handleRiderChange, calculateNischitPremiumForPlanOption, calculateNischitPremiumsBulkForPlanOptions, handlePayment,
    handleSkipPayment, handleCustomerVerificationComplete, saveStepData,
  ]);
};
